import React, { useState, useMemo, useEffect } from "react";
import { API_ENDPOINTS } from "../../config";
import {
  QuestionnaireData,
  ProfessionalCredentials,
  THERAPY_MODALITIES,
  CLINICAL_SPECIALIZATIONS,
  TherapyModality,
  ClinicalSpecialization,
  CLIENT_AGE_GROUPS,
  SESSION_TYPES,
  GENDER_PREFERENCES,
  POPULATION_SPECIALIZATIONS,
  ClientPreferences,
  SessionTypeWithExperience,
  GenderPreference,
  PopulationSpecialization,
  CheckboxItem,
  SessionTypeName,
  ClientAgeGroupName,
  GenderPreferenceName,
  PopulationSpecializationName,
  RELIGIOUS_BACKGROUNDS,
  CULTURAL_BACKGROUNDS,
  LANGUAGES,
  SEXUAL_ORIENTATIONS,
  ReligiousBackground,
  CulturalBackground,
  Language,
  SexualOrientation,
  PersonalBackground,
  THERAPEUTIC_STYLES,
  SPIRITUALITY_APPROACHES,
  NON_TREATMENT_ISSUES,
  TherapeuticStyle,
  SpiritualityApproach,
  NonTreatmentIssue,
  PracticeApproach,
  INSURANCE_PROVIDERS,
  InsuranceProvider,
  PROFESSIONAL_MEMBERSHIPS,
  ProfessionalMembership,
  AdditionalInformation,
  QuestionData,
} from "../../types";
import CheckboxGroup from "./CheckboxGroup";
import { useApp } from "../../context/AppContext";
import { VocreaLogo } from "../VocreaLogo";
import "../../styles/questionnaire-modern.css";

interface QuestionnairePageProps {
  userType: string;
  onBack: () => void;
  onRegisterSuccess: () => void;
  registrationData?: {
    email: string;
    password: string;
    name: string;
  };
}

const initialProfessionalCredentials: ProfessionalCredentials = {
  fullName: "",
  professionalTitle: "",
  licenseType: "",
  licenseNumber: "",
  licensedStates: "",
  yearsOfExperience: "",
  education: {
    school: "",
    degree: "",
    fieldOfStudy: "",
    graduationYear: "",
  },
  location: {
    city: "",
    state: "",
    postalCode: "",
    address: "",
  },
  fees: {
    individual: "",
    couples: "",
    slidingScale: false,
  },
  insuranceAccepted: [],
  profilePhoto: "",
  selfDescription: "",
  phone: "",
};

const initialTherapeuticSpecializations = {
  topModalities: [] as TherapyModality[],
  allTrainedModalities: [] as TherapyModality[],
  topSpecializations: [] as ClinicalSpecialization[],
  allClinicalAreas: [] as ClinicalSpecialization[],
};

const initialClientPreferences = {
  ageGroups: [] as CheckboxItem[],
  sessionTypes: {
    primary: [] as SessionTypeWithExperience[],
    allTrained: [] as CheckboxItem[],
  },
  genderPreferences: [] as CheckboxItem[],
  populationSpecializations: [] as CheckboxItem[],
};

const initialPersonalBackground: PersonalBackground = {
  religiousBackground: [],
  culturalBackground: [],
  languages: [],
  sexualOrientation: [],
};

const initialPracticeApproach: PracticeApproach = {
  therapeuticStyle: [],
  spiritualityApproach: [],
  nonTreatmentIssues: [],
};

const initialAdditionalInformation: AdditionalInformation = {
  specialCertifications: "",
  professionalMemberships: [],
  additionalNotes: "",
};

const formatCheckboxItems = (items: CheckboxItem[] = []): string[] => {
  return items.map((item) => {
    let text = String(item.name);
    if (item.yearsOfExperience) {
      text += ` (${item.yearsOfExperience} years)`;
    }
    if (item.isOther && item.otherValue) {
      text += `: ${item.otherValue}`;
    }
    return text;
  });
};

const convertToQuestionFormat = (
  formData: QuestionnaireData
): QuestionData[] => {
  const questions: QuestionData[] = [];

  // 1. Professional Credentials (Baseline fields)
  questions.push({
    title: "Professional Credentials",
    type: "text",
    answer: [
      {
        subtitle: "Full Name and Professional Title",
        answer: `${formData.professionalCredentials.fullName}, ${formData.professionalCredentials.professionalTitle}`,
      },
      {
        subtitle: "License Information",
        answer: `Type: ${formData.professionalCredentials.licenseType}, Number: ${formData.professionalCredentials.licenseNumber}, States: ${formData.professionalCredentials.licensedStates}`,
      },
      {
        subtitle: "Years of Experience",
        answer: formData.professionalCredentials.yearsOfExperience,
      },
      {
        subtitle: "Education",
        answer: `${formData.professionalCredentials.education.school} - ${formData.professionalCredentials.education.degree} in ${formData.professionalCredentials.education.fieldOfStudy}, ${formData.professionalCredentials.education.graduationYear}`,
      },
      {
        subtitle: "Primary Office Location",
        answer: `${formData.professionalCredentials.location.address ? formData.professionalCredentials.location.address + ', ' : ''}${formData.professionalCredentials.location.city}, ${formData.professionalCredentials.location.state} ${formData.professionalCredentials.location.postalCode}`,
      },
      {
        subtitle: "Session Fees",
        answer: `Individual: ${formData.professionalCredentials.fees.individual}${formData.professionalCredentials.fees.couples ? ', Couples: ' + formData.professionalCredentials.fees.couples : ''}${formData.professionalCredentials.fees.slidingScale ? ', Sliding Scale: Available' : ''}`,
      },
      {
        subtitle: "Insurance Accepted",
        answer: formData.professionalCredentials.insuranceAccepted.join(", "),
      },
      {
        subtitle: "Professional Photo",
        answer: formData.professionalCredentials.profilePhoto,
      },
      {
        subtitle: "Self Description",
        answer: formData.professionalCredentials.selfDescription,
      },
    ],
  });

  // 2. Therapeutic Specializations
  questions.push({
    title: "Primary Therapy Modalities Top Modalities",
    type: "choice",
    choices: formatCheckboxItems(
      formData.therapeuticSpecializations.topModalities
    ),
  });

  questions.push({
    title: "Primary Therapy Modalities All Trained Therapy Modalities",
    type: "choice",
    choices: formatCheckboxItems(
      formData.therapeuticSpecializations.allTrainedModalities
    ),
  });

  questions.push({
    title: "Clinical Specializations Top Clinical Areas",
    type: "choice",
    choices: formatCheckboxItems(
      formData.therapeuticSpecializations.topSpecializations
    ),
  });

  questions.push({
    title: "Clinical Specializations All Clinical Areas",
    type: "choice",
    choices: formatCheckboxItems(
      formData.therapeuticSpecializations.allClinicalAreas
    ),
  });

  // 3. Client Preferences
  questions.push({
    title: "Preferred Client Age Groups",
    type: "choice",
    choices: formatCheckboxItems(formData.clientPreferences.ageGroups),
  });

  questions.push({
    title: "Session Types You Provide Primary Session Types",
    type: "choice",
    choices: formatCheckboxItems(
      formData.clientPreferences.sessionTypes.primary
    ),
  });

  questions.push({
    title: "Session Types You Provide All Session Types Trained In",
    type: "choice",
    choices: formatCheckboxItems(
      formData.clientPreferences.sessionTypes.allTrained
    ),
  });

  questions.push({
    title: "Gender Preferences for Clients",
    type: "choice",
    choices: formatCheckboxItems(formData.clientPreferences.genderPreferences),
  });

  questions.push({
    title: "Population Specializations",
    type: "choice",
    choices: formatCheckboxItems(
      formData.clientPreferences.populationSpecializations
    ),
  });

  // 4. Cultural & Personal Background
  questions.push({
    title: "Religious/Spiritual Background",
    type: "choice",
    choices: formatCheckboxItems(
      formData.personalBackground.religiousBackground
    ),
  });

  questions.push({
    title: "Cultural/Ethnic Background",
    type: "choice",
    choices: formatCheckboxItems(
      formData.personalBackground.culturalBackground
    ),
  });

  questions.push({
    title: "Languages Spoken",
    type: "choice",
    choices: formatCheckboxItems(formData.personalBackground.languages),
  });

  if (
    formData.personalBackground.sexualOrientation &&
    formData.personalBackground.sexualOrientation.length > 0
  ) {
    questions.push({
      title: "Sexual Orientation",
      type: "choice",
      choices: formatCheckboxItems(
        formData.personalBackground.sexualOrientation
      ),
    });
  }

  // 5. Practice Approach & Philosophy
  questions.push({
    title: "Therapeutic Approach Style",
    type: "choice",
    choices: formatCheckboxItems(formData.practiceApproach.therapeuticStyle),
  });

  questions.push({
    title: "Integration of Spirituality/Religion in Therapy",
    type: "choice",
    choices: formatCheckboxItems(
      formData.practiceApproach.spiritualityApproach
    ),
  });

  questions.push({
    title: "Issues You Do NOT Treat",
    type: "choice",
    choices: formatCheckboxItems(formData.practiceApproach.nonTreatmentIssues),
  });

  // 6. Additional Information
  questions.push({
    title: "Special Certifications or Training",
    type: "text",
    answer: [
      {
        subtitle: "Special Certifications or Training",
        answer:
          formData.additionalInformation.specialCertifications ||
          "Not provided",
      },
    ],
  });

  questions.push({
    title: "Professional Memberships",
    type: "choice",
    choices: formatCheckboxItems(
      formData.additionalInformation.professionalMemberships
    ),
  });

  questions.push({
    title: "Anything else you'd like to say to your clients",
    type: "text",
    answer: [
      {
        subtitle: "Anything else you'd like to say to your clients",
        answer:
          formData.additionalInformation.additionalNotes || "Not provided",
      },
    ],
  });

  return questions;
};

const QuestionnairePage: React.FC<QuestionnairePageProps> = ({
  userType,
  onBack,
  onRegisterSuccess,
  registrationData,
}) => {
  const { dispatch } = useApp();
  const [currentSection, setCurrentSection] = useState<
    | "credentials"
    | "specializations"
    | "preferences"
    | "background"
    | "approach"
    | "additional"
  >("credentials");
  const [formData, setFormData] = useState<QuestionnaireData>({
    professionalCredentials: initialProfessionalCredentials,
    therapeuticSpecializations: initialTherapeuticSpecializations,
    clientPreferences: initialClientPreferences,
    personalBackground: initialPersonalBackground,
    practiceApproach: initialPracticeApproach,
    additionalInformation: initialAdditionalInformation,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Section navigation helpers
  const sections = ["credentials", "specializations", "preferences", "background", "approach", "additional"] as const;
  const totalSteps = sections.length;
  const currentStep = sections.indexOf(currentSection) + 1;
  const progressPercentage = (currentStep / totalSteps) * 100;

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    const credentials = formData.professionalCredentials;
    return (
      // Basic info
      credentials.fullName.trim() !== "" &&
      credentials.professionalTitle.trim() !== "" &&
      credentials.licenseType.trim() !== "" &&
      credentials.licenseNumber.trim() !== "" &&
      credentials.licensedStates.trim() !== "" &&
      credentials.yearsOfExperience !== "" &&
      // Education (including school name)
      credentials.education.school.trim() !== "" &&
      credentials.education.degree !== "" &&
      credentials.education.fieldOfStudy.trim() !== "" &&
      credentials.education.graduationYear !== "" &&
      // Location
      credentials.location.city.trim() !== "" &&
      credentials.location.state.trim() !== "" &&
      credentials.location.postalCode.trim() !== "" &&
      // Fees
      credentials.fees.individual.trim() !== "" &&
      // Insurance (at least select one option including "Out of network")
      credentials.insuranceAccepted.length > 0 &&
      // Profile photo
      credentials.profilePhoto.trim() !== "" &&
      // Self description
      credentials.selfDescription.trim() !== ""
    );
  }, [formData.professionalCredentials]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    if (name.startsWith("education.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        professionalCredentials: {
          ...prev.professionalCredentials,
          education: {
            ...prev.professionalCredentials.education,
            [field]: value,
          },
        },
      }));
    } else if (name.startsWith("location.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        professionalCredentials: {
          ...prev.professionalCredentials,
          location: {
            ...prev.professionalCredentials.location,
            [field]: value,
          },
        },
      }));
    } else if (name.startsWith("fees.")) {
      const field = name.split(".")[1];
      const finalValue = type === "checkbox" ? (e.target as HTMLInputElement).checked : value;
      setFormData((prev) => ({
        ...prev,
        professionalCredentials: {
          ...prev.professionalCredentials,
          fees: {
            ...prev.professionalCredentials.fees,
            [field]: finalValue,
          },
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        professionalCredentials: {
          ...prev.professionalCredentials,
          [name]: value,
        },
      }));
    }
  };

  const handleInsuranceChange = (items: CheckboxItem[]) => {
    setFormData((prev) => ({
      ...prev,
      professionalCredentials: {
        ...prev.professionalCredentials,
        insuranceAccepted: items.map((item) => String(item.name)),
      },
    }));
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          professionalCredentials: {
            ...prev.professionalCredentials,
            profilePhoto: reader.result as string,
          },
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTopModalitiesChange = (items: CheckboxItem[]) => {
    setFormData((prev) => {
      // Get newly added items
      const newItems = items.filter(
        (item) =>
          !prev.therapeuticSpecializations.topModalities.some(
            (prevItem) => prevItem.name === item.name
          )
      );

      // Update allTrainedModalities: add new items, but don't remove existing ones
      const updatedAllTrained = [
        ...prev.therapeuticSpecializations.allTrainedModalities,
      ];
      newItems.forEach((item) => {
        if (!updatedAllTrained.some((trained) => trained.name === item.name)) {
          updatedAllTrained.push({
            ...item,
            name: item.name as (typeof THERAPY_MODALITIES)[number],
          });
        }
      });

      return {
        ...prev,
        therapeuticSpecializations: {
          ...prev.therapeuticSpecializations,
          topModalities: items.map((item) => ({
            ...item,
            name: item.name as (typeof THERAPY_MODALITIES)[number],
          })) as TherapyModality[],
          allTrainedModalities: updatedAllTrained,
        },
      };
    });
  };

  const handleAllTrainedModalitiesChange = (items: CheckboxItem[]) => {
    setFormData((prev) => {
      // Ensure items in topModalities cannot be unchecked
      const updatedItems = items.filter(
        (item) =>
          !prev.therapeuticSpecializations.topModalities.some(
            (topItem) => topItem.name === item.name
          )
      );

      return {
        ...prev,
        therapeuticSpecializations: {
          ...prev.therapeuticSpecializations,
          allTrainedModalities: [
            ...prev.therapeuticSpecializations.topModalities,
            ...updatedItems.map((item) => ({
              ...item,
              name: item.name as (typeof THERAPY_MODALITIES)[number],
            })),
          ] as TherapyModality[],
        },
      };
    });
  };

  const handleTopSpecializationsChange = (items: CheckboxItem[]) => {
    setFormData((prev) => {
      // Get newly added items
      const newItems = items.filter(
        (item) =>
          !prev.therapeuticSpecializations.topSpecializations.some(
            (prevItem) => prevItem.name === item.name
          )
      );

      // Update allClinicalAreas: add new items, but don't remove existing ones
      const updatedAllAreas = [
        ...prev.therapeuticSpecializations.allClinicalAreas,
      ];
      newItems.forEach((item) => {
        if (!updatedAllAreas.some((area) => area.name === item.name)) {
          updatedAllAreas.push({
            ...item,
            name: item.name as (typeof CLINICAL_SPECIALIZATIONS)[number],
          });
        }
      });

      return {
        ...prev,
        therapeuticSpecializations: {
          ...prev.therapeuticSpecializations,
          topSpecializations: items.map((item) => ({
            ...item,
            name: item.name as (typeof CLINICAL_SPECIALIZATIONS)[number],
          })) as ClinicalSpecialization[],
          allClinicalAreas: updatedAllAreas,
        },
      };
    });
  };

  const handleAllClinicalAreasChange = (items: CheckboxItem[]) => {
    setFormData((prev) => {
      // Ensure items in topSpecializations cannot be unchecked
      const updatedItems = items.filter(
        (item) =>
          !prev.therapeuticSpecializations.topSpecializations.some(
            (topItem) => topItem.name === item.name
          )
      );

      return {
        ...prev,
        therapeuticSpecializations: {
          ...prev.therapeuticSpecializations,
          allClinicalAreas: [
            ...prev.therapeuticSpecializations.topSpecializations,
            ...updatedItems.map((item) => ({
              ...item,
              name: item.name as (typeof CLINICAL_SPECIALIZATIONS)[number],
            })),
          ] as ClinicalSpecialization[],
        },
      };
    });
  };

  const isSpecializationsValid = useMemo(() => {
    const specializations = formData.therapeuticSpecializations;

    // Check topModalities selection and years
    const hasValidTopModalities =
      specializations.topModalities.length >= 1 &&
      specializations.topModalities.length <= 3 &&
      specializations.topModalities.every(
        (item) =>
          (!item.isOther ||
            (item.otherValue && item.otherValue.trim() !== "")) &&
          typeof item.yearsOfExperience === "number" &&
          item.yearsOfExperience >= 0
      );

    // Check allTrainedModalities selection
    const hasValidAllModalities =
      specializations.allTrainedModalities.length > 0 &&
      specializations.topModalities.every((topItem) =>
        specializations.allTrainedModalities.some(
          (allItem) => allItem.name === topItem.name
        )
      );

    // Check topSpecializations selection and years
    const hasValidTopSpecializations =
      specializations.topSpecializations.length >= 1 &&
      specializations.topSpecializations.length <= 3 &&
      specializations.topSpecializations.every(
        (item) =>
          (!item.isOther ||
            (item.otherValue && item.otherValue.trim() !== "")) &&
          typeof item.yearsOfExperience === "number" &&
          item.yearsOfExperience >= 0
      );

    // Check allClinicalAreas selection
    const hasValidAllSpecializations =
      specializations.allClinicalAreas.length > 0 &&
      specializations.topSpecializations.every((topItem) =>
        specializations.allClinicalAreas.some(
          (allItem) => allItem.name === topItem.name
        )
      );

    return (
      hasValidTopModalities &&
      hasValidAllModalities &&
      hasValidTopSpecializations &&
      hasValidAllSpecializations
    );
  }, [formData.therapeuticSpecializations]);

  const getSpecializationsError = () => {
    const specializations = formData.therapeuticSpecializations;

    if (specializations.topModalities.length === 0) {
      return "Please select 1-3 primary therapeutic modalities in section 5a";
    }
    if (specializations.topModalities.length > 3) {
      return "You can only select up to 3 primary therapeutic modalities in section 5a";
    }
    if (
      specializations.topModalities.some(
        (item) =>
          typeof item.yearsOfExperience !== "number" ||
          item.yearsOfExperience < 0
      )
    ) {
      return "Please fill in years of clinical experience for all selected options in section 5a";
    }
    if (
      specializations.topModalities.some(
        (item) =>
          item.isOther && (!item.otherValue || item.otherValue.trim() === "")
      )
    ) {
      return "Please specify the content for 'Other' option in section 5a";
    }

    if (specializations.allTrainedModalities.length === 0) {
      return "Please select your trained therapeutic modalities in section 5b";
    }
    if (
      !specializations.topModalities.every((topItem) =>
        specializations.allTrainedModalities.some(
          (allItem) => allItem.name === topItem.name
        )
      )
    ) {
      return "Section 5b must include all modalities selected in section 5a";
    }

    if (specializations.topSpecializations.length === 0) {
      return "Please select 1-3 primary clinical specializations in section 6a";
    }
    if (specializations.topSpecializations.length > 3) {
      return "You can only select up to 3 primary clinical specializations in section 6a";
    }
    if (
      specializations.topSpecializations.some(
        (item) =>
          typeof item.yearsOfExperience !== "number" ||
          item.yearsOfExperience < 0
      )
    ) {
      return "Please fill in years of clinical experience for all selected options in section 6a";
    }
    if (
      specializations.topSpecializations.some(
        (item) =>
          item.isOther && (!item.otherValue || item.otherValue.trim() === "")
      )
    ) {
      return "Please specify the content for 'Other' option in section 6a";
    }

    if (specializations.allClinicalAreas.length === 0) {
      return "Please select your trained clinical areas in section 6b";
    }
    if (
      !specializations.topSpecializations.every((topItem) =>
        specializations.allClinicalAreas.some(
          (allItem) => allItem.name === topItem.name
        )
      )
    ) {
      return "Section 6b must include all specializations selected in section 6a";
    }

    return "";
  };

  const isPreferencesValid = useMemo(() => {
    const preferences = formData.clientPreferences;

    // Check if at least one age group is selected
    const hasAgeGroups = preferences.ageGroups.length > 0;

    // Check primary session types selection and years
    const hasValidPrimarySessionTypes =
      preferences.sessionTypes.primary.length >= 1 &&
      preferences.sessionTypes.primary.length <= 3 &&
      preferences.sessionTypes.primary.every(
        (item) =>
          typeof item.yearsOfExperience === "number" &&
          item.yearsOfExperience >= 0
      );

    // Check all trained session types
    const hasValidAllSessionTypes =
      preferences.sessionTypes.allTrained.length >=
        preferences.sessionTypes.primary.length &&
      preferences.sessionTypes.primary.every((primaryItem) =>
        preferences.sessionTypes.allTrained.some(
          (allItem) => allItem.name === primaryItem.name
        )
      );

    // Check if at least one gender preference is selected and Other has value
    const hasValidGenderPreferences =
      preferences.genderPreferences.length > 0 &&
      preferences.genderPreferences.every(
        (item) =>
          !item.isOther ||
          (item.isOther && item.otherValue && item.otherValue.trim() !== "")
      );

    // Check if at least one population specialization is selected
    const hasValidPopulationSpecializations =
      preferences.populationSpecializations.length > 0;

    return (
      hasAgeGroups &&
      hasValidPrimarySessionTypes &&
      hasValidAllSessionTypes &&
      hasValidGenderPreferences &&
      hasValidPopulationSpecializations
    );
  }, [formData.clientPreferences]);

  const getPreferencesError = () => {
    const preferences = formData.clientPreferences;

    if (preferences.ageGroups.length === 0) {
      return "Please select at least one preferred client age group";
    }

    if (preferences.sessionTypes.primary.length === 0) {
      return "Please select 1-3 primary session types in section 8a";
    }
    if (preferences.sessionTypes.primary.length > 3) {
      return "You can only select up to 3 primary session types in section 8a";
    }
    if (
      preferences.sessionTypes.primary.some(
        (item) =>
          typeof item.yearsOfExperience !== "number" ||
          item.yearsOfExperience < 0
      )
    ) {
      return "Please fill in years of clinical experience for all selected session types in section 8a";
    }

    if (preferences.sessionTypes.allTrained.length === 0) {
      return "Please select your trained session types in section 8b";
    }
    if (
      !preferences.sessionTypes.primary.every((primaryItem) =>
        preferences.sessionTypes.allTrained.some(
          (allItem) => allItem.name === primaryItem.name
        )
      )
    ) {
      return "Section 8b must include all session types selected in section 8a";
    }

    if (preferences.genderPreferences.length === 0) {
      return "Please select at least one gender preference";
    }
    if (
      preferences.genderPreferences.some(
        (item) =>
          item.isOther && (!item.otherValue || item.otherValue.trim() === "")
      )
    ) {
      return "Please specify your other gender preference";
    }

    if (preferences.populationSpecializations.length === 0) {
      return "Please select at least one population specialization";
    }

    return "";
  };

  const handleAgeGroupsChange = (items: CheckboxItem[]) => {
    setFormData((prev) => ({
      ...prev,
      clientPreferences: {
        ...prev.clientPreferences,
        ageGroups: items.map((item) => ({
          ...item,
          name: item.name as ClientAgeGroupName,
        })),
      },
    }));
  };

  const handleSessionTypesChange = (
    field: "primary" | "allTrained",
    items: CheckboxItem[]
  ) => {
    if (field === "primary") {
      const typedItems = items.map((item) => ({
        ...item,
        name: item.name as SessionTypeName,
        yearsOfExperience: item.yearsOfExperience || 0,
      })) as SessionTypeWithExperience[];
      setFormData((prev) => {
        // Get previously selected items in 8b
        const previousAllTrained =
          prev.clientPreferences.sessionTypes.allTrained;

        // Create a new set of 8b options, including previous and new primary items
        const newAllTrained = [...previousAllTrained];

        // Ensure all new primary items are in allTrained
        typedItems.forEach((primaryItem) => {
          if (
            !newAllTrained.some((allItem) => allItem.name === primaryItem.name)
          ) {
            newAllTrained.push({
              name: primaryItem.name,
              isOther: primaryItem.isOther,
              otherValue: primaryItem.otherValue,
            });
          }
        });

        return {
          ...prev,
          clientPreferences: {
            ...prev.clientPreferences,
            sessionTypes: {
              primary: typedItems,
              allTrained: newAllTrained,
            },
          },
        };
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        clientPreferences: {
          ...prev.clientPreferences,
          sessionTypes: {
            ...prev.clientPreferences.sessionTypes,
            allTrained: items,
          },
        },
      }));
    }
  };

  const handleGenderPreferencesChange = (items: CheckboxItem[]) => {
    setFormData((prev) => ({
      ...prev,
      clientPreferences: {
        ...prev.clientPreferences,
        genderPreferences: items.map((item) => ({
          ...item,
          name: item.name as GenderPreferenceName,
        })),
      },
    }));
  };

  const handlePopulationSpecializationsChange = (items: CheckboxItem[]) => {
    setFormData((prev) => ({
      ...prev,
      clientPreferences: {
        ...prev.clientPreferences,
        populationSpecializations: items.map((item) => ({
          ...item,
          name: item.name as PopulationSpecializationName,
        })),
      },
    }));
  };

  const isBackgroundValid = useMemo(() => {
    const background = formData.personalBackground;

    // Only check required field: languages
    const hasValidLanguages = background.languages.length > 0;

    return hasValidLanguages;
  }, [formData.personalBackground]);

  const getBackgroundError = () => {
    const background = formData.personalBackground;

    if (background.languages.length === 0) {
      return "Please select at least one language";
    }

    return "";
  };

  const handleReligiousBackgroundChange = (items: CheckboxItem[]) => {
    setFormData((prev) => ({
      ...prev,
      personalBackground: {
        ...prev.personalBackground,
        religiousBackground: items.map((item) => ({
          ...item,
          name: item.name as ReligiousBackground,
        })),
      },
    }));
  };

  const handleCulturalBackgroundChange = (items: CheckboxItem[]) => {
    setFormData((prev) => ({
      ...prev,
      personalBackground: {
        ...prev.personalBackground,
        culturalBackground: items.map((item) => ({
          ...item,
          name: item.name as CulturalBackground,
        })),
      },
    }));
  };

  const handleLanguagesChange = (items: CheckboxItem[]) => {
    setFormData((prev) => ({
      ...prev,
      personalBackground: {
        ...prev.personalBackground,
        languages: items.map((item) => ({
          ...item,
          name: item.name as Language,
        })),
      },
    }));
  };

  const handleSexualOrientationChange = (items: CheckboxItem[]) => {
    setFormData((prev) => ({
      ...prev,
      personalBackground: {
        ...prev.personalBackground,
        sexualOrientation: items.map((item) => ({
          ...item,
          name: item.name as SexualOrientation,
        })),
      },
    }));
  };

  const isApproachValid = useMemo(() => {
    const approach = formData.practiceApproach;

    // Check if exactly one therapeutic style is selected
    const hasValidStyle = approach.therapeuticStyle.length === 1;

    // Check if exactly one spirituality approach is selected
    const hasValidSpirituality = approach.spiritualityApproach.length === 1;

    return hasValidStyle && hasValidSpirituality;
  }, [formData.practiceApproach]);

  const getApproachError = () => {
    const approach = formData.practiceApproach;

    if (approach.therapeuticStyle.length === 0) {
      return "Please select your therapeutic approach style";
    }
    if (approach.therapeuticStyle.length > 1) {
      return "Please select only one therapeutic approach style";
    }

    if (approach.spiritualityApproach.length === 0) {
      return "Please select your approach to spirituality/religion in therapy";
    }
    if (approach.spiritualityApproach.length > 1) {
      return "Please select only one approach to spirituality/religion";
    }

    return "";
  };

  const handleTherapeuticStyleChange = (items: CheckboxItem[]) => {
    setFormData((prev) => ({
      ...prev,
      practiceApproach: {
        ...prev.practiceApproach,
        therapeuticStyle: items.map((item) => ({
          ...item,
          name: item.name as TherapeuticStyle,
        })),
      },
    }));
  };

  const handleSpiritualityApproachChange = (items: CheckboxItem[]) => {
    setFormData((prev) => ({
      ...prev,
      practiceApproach: {
        ...prev.practiceApproach,
        spiritualityApproach: items.map((item) => ({
          ...item,
          name: item.name as SpiritualityApproach,
        })),
      },
    }));
  };

  const handleNonTreatmentIssuesChange = (items: CheckboxItem[]) => {
    setFormData((prev) => ({
      ...prev,
      practiceApproach: {
        ...prev.practiceApproach,
        nonTreatmentIssues: items.map((item) => ({
          ...item,
          name: item.name as NonTreatmentIssue,
        })),
      },
    }));
  };

  const handleProfessionalMembershipsChange = (items: CheckboxItem[]) => {
    setFormData((prev) => ({
      ...prev,
      additionalInformation: {
        ...prev.additionalInformation,
        professionalMemberships: items,
      },
    }));
  };

  const handleSpecialCertificationsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      additionalInformation: {
        ...prev.additionalInformation,
        specialCertifications: e.target.value,
      },
    }));
  };

  const handleAdditionalNotesChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      additionalInformation: {
        ...prev.additionalInformation,
        additionalNotes: e.target.value,
      },
    }));
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleNext = () => {
    if (currentSection === "credentials" && isFormValid) {
      setCurrentSection("specializations");
      scrollToTop();
    } else if (currentSection === "specializations" && isSpecializationsValid) {
      setCurrentSection("preferences");
      scrollToTop();
    } else if (currentSection === "preferences" && isPreferencesValid) {
      setCurrentSection("background");
      scrollToTop();
    } else if (currentSection === "background" && isBackgroundValid) {
      setCurrentSection("approach");
      scrollToTop();
    } else if (currentSection === "approach" && isApproachValid) {
      setCurrentSection("additional");
      scrollToTop();
    } else if (currentSection === "additional") {
      // Handle form submission
      const finalFormData = {
        ...formData,
        practiceApproach: {
          ...formData.practiceApproach,
          nonTreatmentIssues: formData.practiceApproach.nonTreatmentIssues.map(
            (item) => ({
              ...item,
              value:
                item.isOther && item.otherValue
                  ? item.otherValue
                  : String(item.name),
            })
          ),
        },
        additionalInformation: {
          ...formData.additionalInformation,
          professionalMemberships:
            formData.additionalInformation.professionalMemberships.map(
              (item) => ({
                ...item,
                value:
                  item.isOther && item.otherValue
                    ? item.otherValue
                    : String(item.name),
              })
            ),
        },
      } as QuestionnaireData;

      // Convert to question-answer format
      const questionFormat = convertToQuestionFormat(finalFormData);

      // Call registration API
      submitRegistration(questionFormat);
    }
  };

  const handleBack = () => {
    if (currentSection === "specializations") {
      setCurrentSection("credentials");
      scrollToTop();
    } else if (currentSection === "preferences") {
      setCurrentSection("specializations");
      scrollToTop();
    } else if (currentSection === "background") {
      setCurrentSection("preferences");
      scrollToTop();
    } else if (currentSection === "approach") {
      setCurrentSection("background");
      scrollToTop();
    } else if (currentSection === "additional") {
      setCurrentSection("approach");
      scrollToTop();
    } else {
      onBack();
    }
  };

  const submitRegistration = async (questionFormat: QuestionData[]) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Get the JWT token (user is already registered and verified)
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("Authentication required. Please log in again.");
      }

      // Convert questionnaire data to therapist profile format
      const profileData: any = {
        name: formData.professionalCredentials.fullName,
        title: formData.professionalCredentials.professionalTitle,
        phone: formData.professionalCredentials.phone || null,
        self_description: formData.professionalCredentials.selfDescription,
        years_practicing: formData.professionalCredentials.yearsOfExperience,

        // Education
        education: [{
          school: formData.professionalCredentials.education.school,
          degree: formData.professionalCredentials.education.degree,
          field_of_study: formData.professionalCredentials.education.fieldOfStudy,
          graduation_year: formData.professionalCredentials.education.graduationYear,
        }],

        // Specialties
        specialties: formatCheckboxItems(formData.therapeuticSpecializations.topSpecializations),

        // Treatment approaches
        treatment_approach: formatCheckboxItems(formData.therapeuticSpecializations.topModalities),

        // Languages
        languages: formatCheckboxItems(formData.personalBackground.languages),

        // Insurance
        insurance_accepted: formData.professionalCredentials.insuranceAccepted,

        // Session fees - simple format matching JSON providers and scraper
        session_fee: {
          "Individual": formData.professionalCredentials.fees.individual || null,
          "Couple": formData.professionalCredentials.fees.couples || null,
          "Sliding Scale": formData.professionalCredentials.fees.slidingScale ? "Available" : null,
        },

        // Profile photo
        photo_urls: formData.professionalCredentials.profilePhoto ?
          [formData.professionalCredentials.profilePhoto] : [],

        // Licenses - only include non-empty fields
        licenses: formData.professionalCredentials.licensedStates.split(',').map((state: string) => {
          const license: any = { state: state.trim() };
          if (formData.professionalCredentials.licenseType) {
            license.license_type = formData.professionalCredentials.licenseType;
          }
          if (formData.professionalCredentials.licenseNumber) {
            license.license_number = formData.professionalCredentials.licenseNumber;
          }
          return license;
        }),

        // Primary office location - only include non-empty fields
        locations: [{
          ...(formData.professionalCredentials.location.address && { address_line: formData.professionalCredentials.location.address }),
          city: formData.professionalCredentials.location.city,
          state: formData.professionalCredentials.location.state,
          postal_code: formData.professionalCredentials.location.postalCode,
          country: "USA",
          in_person: true,
          is_primary: true,
        }],
      };

      // Update therapist profile
      const res = await fetch(API_ENDPOINTS.UPDATE_THERAPIST_PROFILE, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(profileData),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error("[QuestionnairePage] Profile update failed:", data);
        throw new Error(data.error || "Failed to update profile. Please try again.");
      }

      // Clear temporarily stored registration information (legacy cleanup)
      localStorage.removeItem("temp_register_email");
      localStorage.removeItem("temp_register_password");
      localStorage.removeItem("temp_register_name");

      // Fetch user info to complete login
      const userInfoRes = await fetch(API_ENDPOINTS.USERS_INFO, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!userInfoRes.ok) {
        throw new Error("Failed to get user information");
      }

      const userInfoData = await userInfoRes.json();

      // Get user ID from server response (secure)
      const userId = userInfoData.id;

      if (!userId) {
        throw new Error("User ID not found in server response");
      }

      // Set user information in AppContext to complete login
      dispatch({
        type: "SET_CURRENT_USER",
        payload: userId,
      });

      dispatch({
        type: "SET_USER",
        payload: {
          id: userId,
          name: userInfoData.name || "user",
          email: userInfoData.email,
          role: (userInfoData.type as "therapist" | "normal") || "normal",
          has_paid: userInfoData.has_paid || false,
          profile_data: userInfoData.profile_data,
          profile_photo_url: userInfoData.profile_photo_url,
        },
      });

      // Profile updated and user logged in, complete registration flow
      setIsSubmitting(false);
      onRegisterSuccess();
    } catch (error: any) {
      console.error("Profile update failed:", error);
      setError(error.message || "Failed to update profile. Please try again.");
      setIsSubmitting(false);
      scrollToTop();
    }
  };

  useEffect(() => {
    scrollToTop();
  }, [currentSection]);

  if (currentSection === "credentials") {
    return (
      <div className="questionnaire-container-modern">
        <div className="questionnaire-wrapper-modern">
          {/* Header with Logo */}
          <div className="questionnaire-header-modern">
            <div className="questionnaire-logo-title">
              <VocreaLogo />
              <h1 className="questionnaire-app-title">Vocrea AI</h1>
            </div>
          </div>

          {/* Main Card */}
          <div className="questionnaire-card-modern">
            {/* Progress Bar */}
            <div className="questionnaire-progress-section">
              <div className="questionnaire-progress-header">
                <button className="questionnaire-back-button" onClick={handleBack}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18L9 12L15 6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Back</span>
                </button>
                <span className="questionnaire-step-indicator">
                  Step {currentStep} of {totalSteps}
                </span>
              </div>
              <div className="questionnaire-progress-bar-container">
                <div className="questionnaire-progress-bar" style={{ width: `${progressPercentage}%` }} />
              </div>
            </div>

            {/* Form Content */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleNext();
              }}
              className="questionnaire-form-modern"
            >
              <div className="questionnaire-step-content">
                <div className="questionnaire-step-header">
                  <h2 className="questionnaire-step-title">Professional Credentials</h2>
                  <p className="questionnaire-step-subtitle">Let's start with your professional information</p>
                </div>

              <div className="questionnaire-form-group">
                <label htmlFor="fullName" className="questionnaire-label">
                  1. Full Name & Professional Title
                </label>
                <input className="questionnaire-input" type="text"
                  id="fullName"
                  name="fullName"
                  placeholder="Full Name"
                  
                  value={formData.professionalCredentials.fullName}
                  onChange={handleInputChange}
                  required
                />
                <input className="questionnaire-input" type="text"
                  id="professionalTitle"
                  name="professionalTitle"
                  placeholder="Professional Title (e.g., LCSW, PhD, PsyD)"
                  
                  value={formData.professionalCredentials.professionalTitle}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="questionnaire-form-group">
                <label className="questionnaire-label">2. License Information</label>
                <input className="questionnaire-input" type="text"
                  id="licenseType"
                  name="licenseType"
                  placeholder="License Type (LCSW, LMFT, LPC, Psychologist, etc.)"
                  
                  value={formData.professionalCredentials.licenseType}
                  onChange={handleInputChange}
                  required
                />
                <input className="questionnaire-input" type="text"
                  id="licenseNumber"
                  name="licenseNumber"
                  placeholder="License Number"
                  
                  value={formData.professionalCredentials.licenseNumber}
                  onChange={handleInputChange}
                  required
                />
                <input className="questionnaire-input" type="text"
                  id="licensedStates"
                  name="licensedStates"
                  placeholder="State(s) where you are licensed to practice (e.g., NY, NJ, CT)"
                  
                  value={formData.professionalCredentials.licensedStates}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="questionnaire-form-group">
                <label className="questionnaire-label" htmlFor="yearsOfExperience">3. Years of Experience</label>
                <input className="questionnaire-input" type="number"
                  id="yearsOfExperience"
                  name="yearsOfExperience"
                  placeholder="Years of practice as a licensed therapist"
                  value={formData.professionalCredentials.yearsOfExperience}
                  onChange={handleInputChange}
                  min="0"
                  required
                />
              </div>

              <div className="questionnaire-form-group">
                <label className="questionnaire-label" htmlFor="phone">4. Phone Number</label>
                <input className="questionnaire-input" type="tel"
                  id="phone"
                  name="phone"
                  placeholder="(555) 123-4567"
                  value={formData.professionalCredentials.phone}
                  onChange={handleInputChange}
                />
              </div>

              <div className="questionnaire-form-group">
                <label className="questionnaire-label" htmlFor="profilePhoto">5. Professional Photo</label>
                <p className="section-description">
                  Upload a professional headshot
                </p>
                <input className="questionnaire-input" type="file"
                  id="profilePhoto"
                  name="profilePhoto"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  required
                />
                {formData.professionalCredentials.profilePhoto && (
                  <img
                    src={formData.professionalCredentials.profilePhoto}
                    alt="Profile preview"
                    style={{ width: "150px", marginTop: "10px", borderRadius: "8px" }}
                  />
                )}
              </div>

              <div className="questionnaire-form-group">
                <label className="questionnaire-label" htmlFor="selfDescription">6. About You (Self Description)</label>
                <p className="section-description">
                  Write a brief description about yourself, your therapeutic approach, and what clients can expect working with you (2-3 paragraphs)
                </p>
                <textarea className="questionnaire-textarea" id="selfDescription"
                  name="selfDescription"

                  value={formData.professionalCredentials.selfDescription}
                  onChange={handleInputChange}
                  rows={6}
                  placeholder="Tell potential clients about yourself..."
                  required
                />
              </div>

              <div className="questionnaire-form-group">
                <label className="questionnaire-label">7. Education & Training</label>
                <input className="questionnaire-input" type="text"
                  id="education.school"
                  name="education.school"
                  placeholder="School/University Name"
                  value={formData.professionalCredentials.education.school}
                  onChange={handleInputChange}
                  required
                />
                <select className="questionnaire-select" id="education.degree"
                  name="education.degree"
                  value={formData.professionalCredentials.education.degree}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Degree</option>
                  <option value="Masters">Master's</option>
                  <option value="Doctorate">Doctorate</option>
                  <option value="Other">Other</option>
                </select>
                <input className="questionnaire-input" type="text"
                  id="education.fieldOfStudy"
                  name="education.fieldOfStudy"
                  placeholder="Field of Study"
                  value={
                    formData.professionalCredentials.education.fieldOfStudy
                  }
                  onChange={handleInputChange}
                  required
                />
                <input className="questionnaire-input" type="number"
                  id="education.graduationYear"
                  name="education.graduationYear"
                  placeholder="Year Graduated"
                  value={
                    formData.professionalCredentials.education.graduationYear
                  }
                  onChange={handleInputChange}
                  min="1900"
                  max={new Date().getFullYear()}
                  required
                />
              </div>

              <div className="questionnaire-form-group">
                <label className="questionnaire-label">8. Primary Office Location</label>
                <input className="questionnaire-input" type="text"
                  id="location.address"
                  name="location.address"
                  placeholder="Street Address (Optional)"
                  value={formData.professionalCredentials.location.address}
                  onChange={handleInputChange}
                />
                <input className="questionnaire-input" type="text"
                  id="location.city"
                  name="location.city"
                  placeholder="City"
                  value={formData.professionalCredentials.location.city}
                  onChange={handleInputChange}
                  required
                />
                <input className="questionnaire-input" type="text"
                  id="location.state"
                  name="location.state"
                  placeholder="State (e.g., NY)"
                  value={formData.professionalCredentials.location.state}
                  onChange={handleInputChange}
                  required
                />
                <input className="questionnaire-input" type="text"
                  id="location.postalCode"
                  name="location.postalCode"
                  placeholder="ZIP Code"
                  value={formData.professionalCredentials.location.postalCode}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="questionnaire-form-group">
                <label className="questionnaire-label">9. Session Fees</label>
                <input className="questionnaire-input" type="text"
                  id="fees.individual"
                  name="fees.individual"
                  placeholder="Individual Session Fee (e.g., $150)"
                  value={formData.professionalCredentials.fees.individual}
                  onChange={handleInputChange}
                  required
                />
                <input className="questionnaire-input" type="text"
                  id="fees.couples"
                  name="fees.couples"
                  placeholder="Couples Session Fee (Optional)"
                  value={formData.professionalCredentials.fees.couples}
                  onChange={handleInputChange}
                />
                <div className="questionnaire-checkbox-row">
                  <label htmlFor="fees.slidingScale">Offer Sliding Scale?</label>
                  <input type="checkbox"
                    id="fees.slidingScale"
                    name="fees.slidingScale"
                    checked={formData.professionalCredentials.fees.slidingScale}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="specialization-group">
                <h3 className="section-subtitle">10. Insurance Accepted</h3>
                <p className="section-description">Select all that apply</p>
                <CheckboxGroup
                  items={INSURANCE_PROVIDERS}
                  selectedItems={formData.professionalCredentials.insuranceAccepted.map(
                    (name) => ({ name })
                  )}
                  onSelectionChange={handleInsuranceChange}
                />
              </div>
              </div>

              <button
                type="submit"
                className="questionnaire-button-primary"
                disabled={!isFormValid}
              >
                Next
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (currentSection === "specializations") {
    return (
      <div className="questionnaire-container-modern">
        <div className="questionnaire-wrapper-modern">
          {/* Header with Logo */}
          <div className="questionnaire-header-modern">
            <div className="questionnaire-logo-title">
              <VocreaLogo />
              <h1 className="questionnaire-app-title">Vocrea AI</h1>
            </div>
          </div>

          {/* Main Card */}
          <div className="questionnaire-card-modern">
            {/* Progress Bar */}
            <div className="questionnaire-progress-section">
              <div className="questionnaire-progress-header">
                <button className="questionnaire-back-button" onClick={handleBack}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18L9 12L15 6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Back</span>
                </button>
                <span className="questionnaire-step-indicator">
                  Step {currentStep} of {totalSteps}
                </span>
              </div>
              <div className="questionnaire-progress-bar-container">
                <div className="questionnaire-progress-bar" style={{ width: `${progressPercentage}%` }} />
              </div>
            </div>

            {/* Section Title */}
            <h2 className="questionnaire-section-title">Therapeutic Specializations</h2>

            {/* Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (isSpecializationsValid) {
                  handleNext();
                }
              }}
              className="questionnaire-form-modern"
            >
              <h3 className="section-subtitle">
                5. Primary Therapeutic Modalities
              </h3>

              <div className="specialization-group">
                <h4 className="section-subsubtitle">5a. Top Modalities</h4>
                <p className="section-description">
                  Select your TOP 1-3 modalities you are most confident in
                </p>
                <CheckboxGroup
                  items={THERAPY_MODALITIES}
                  selectedItems={
                    formData.therapeuticSpecializations.topModalities
                  }
                  onSelectionChange={handleTopModalitiesChange}
                  maxSelections={3}
                  minSelections={1}
                  showYearsInput
                  allowOther
                />
              </div>

              <div className="specialization-group">
                <h4 className="section-subsubtitle">
                  5b. All Trained Modalities
                </h4>
                <p className="section-description">
                  Select ALL modalities you have been trained in, including
                  those above
                </p>
                <CheckboxGroup
                  items={THERAPY_MODALITIES}
                  selectedItems={
                    formData.therapeuticSpecializations.allTrainedModalities
                  }
                  onSelectionChange={handleAllTrainedModalitiesChange}
                  allowOther
                  lockedItems={formData.therapeuticSpecializations.topModalities.map(
                    (item) => item.name
                  )}
                />
              </div>

              <h3 className="section-subtitle">6. Clinical Specializations</h3>

              <div className="specialization-group">
                <h4 className="section-subsubtitle">6a. Top Clinical Areas</h4>
                <p className="section-description">
                  Select your TOP 1-3 clinical areas you are most confident in
                </p>
                <CheckboxGroup
                  items={CLINICAL_SPECIALIZATIONS}
                  selectedItems={
                    formData.therapeuticSpecializations.topSpecializations
                  }
                  onSelectionChange={handleTopSpecializationsChange}
                  maxSelections={3}
                  minSelections={1}
                  showYearsInput
                  allowOther
                />
              </div>

              <div className="specialization-group">
                <h4 className="section-subsubtitle">6b. All Clinical Areas</h4>
                <p className="section-description">
                  Select ALL areas you have training or experience in, including
                  those above
                </p>
                <CheckboxGroup
                  items={CLINICAL_SPECIALIZATIONS}
                  selectedItems={
                    formData.therapeuticSpecializations.allClinicalAreas
                  }
                  onSelectionChange={handleAllClinicalAreasChange}
                  allowOther
                  lockedItems={formData.therapeuticSpecializations.topSpecializations.map(
                    (item) => item.name
                  )}
                />
              </div>

              {!isSpecializationsValid && (
                <div className="questionnaire-error-message">{getSpecializationsError()}</div>
              )}

              <button
                type="submit"
                className="questionnaire-button-primary"
                disabled={!isSpecializationsValid}
              >
                Next
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (currentSection === "preferences") {
    return (
      <div className="questionnaire-container-modern">
        <div className="questionnaire-wrapper-modern">
          {/* Header with Logo */}
          <div className="questionnaire-header-modern">
            <div className="questionnaire-logo-title">
              <VocreaLogo />
              <h1 className="questionnaire-app-title">Vocrea AI</h1>
            </div>
          </div>

          {/* Main Card */}
          <div className="questionnaire-card-modern">
            {/* Progress Bar */}
            <div className="questionnaire-progress-section">
              <div className="questionnaire-progress-header">
                <button className="questionnaire-back-button" onClick={handleBack}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18L9 12L15 6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Back</span>
                </button>
                <span className="questionnaire-step-indicator">
                  Step {currentStep} of {totalSteps}
                </span>
              </div>
              <div className="questionnaire-progress-bar-container">
                <div className="questionnaire-progress-bar" style={{ width: `${progressPercentage}%` }} />
              </div>
            </div>

            {/* Section Title */}
            <h2 className="questionnaire-section-title">Client Preferences</h2>

            {/* Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (isPreferencesValid) {
                  handleNext();
                }
              }}
              className="questionnaire-form-modern"
            >

              <div className="specialization-group">
                <h3 className="section-subtitle">
                  7. Preferred Client Age Groups
                </h3>
                <p className="section-description">Select all that apply</p>
                <CheckboxGroup
                  items={CLIENT_AGE_GROUPS}
                  selectedItems={formData.clientPreferences.ageGroups}
                  onSelectionChange={handleAgeGroupsChange}
                />
              </div>

              <h3 className="section-subtitle">8. Session Types You Provide</h3>

              <div className="specialization-group">
                <h4 className="section-subsubtitle">
                  8a. Primary Session Types
                </h4>
                <p className="section-description">
                  Select your TOP 1-3 session types you are most confident in
                </p>
                <CheckboxGroup
                  items={SESSION_TYPES}
                  selectedItems={
                    formData.clientPreferences.sessionTypes.primary
                  }
                  maxSelections={3}
                  minSelections={1}
                  showYearsInput
                  onSelectionChange={(items) =>
                    handleSessionTypesChange("primary", items)
                  }
                />
              </div>

              <div className="specialization-group">
                <h4 className="section-subsubtitle">
                  8b. All Session Types Trained In
                </h4>
                <p className="section-description">
                  Select ALL session types you have training or experience in,
                  including those above
                </p>
                <CheckboxGroup
                  items={SESSION_TYPES}
                  selectedItems={
                    formData.clientPreferences.sessionTypes.allTrained
                  }
                  onSelectionChange={(items) =>
                    handleSessionTypesChange("allTrained", items)
                  }
                  lockedItems={formData.clientPreferences.sessionTypes.primary.map(
                    (item) => item.name
                  )}
                />
              </div>

              <div className="specialization-group">
                <h3 className="section-subtitle">
                  9. Gender Preferences for Clients
                </h3>
                <p className="section-description">Select all that apply</p>
                <CheckboxGroup
                  items={GENDER_PREFERENCES}
                  selectedItems={formData.clientPreferences.genderPreferences}
                  allowOther
                  onSelectionChange={handleGenderPreferencesChange}
                />
              </div>

              <div className="specialization-group">
                <h3 className="section-subtitle">
                  10. Population Specializations
                </h3>
                <p className="section-description">Select all that apply</p>
                <CheckboxGroup
                  items={POPULATION_SPECIALIZATIONS}
                  selectedItems={
                    formData.clientPreferences.populationSpecializations
                  }
                  onSelectionChange={handlePopulationSpecializationsChange}
                />
              </div>

              {!isPreferencesValid && (
                <div className="questionnaire-error-message">{getPreferencesError()}</div>
              )}

              <button
                type="submit"
                className="questionnaire-button-primary"
                disabled={!isPreferencesValid}
              >
                Next
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (currentSection === "background") {
    return (
      <div className="questionnaire-container-modern">
        <div className="questionnaire-wrapper-modern">
          {/* Header with Logo */}
          <div className="questionnaire-header-modern">
            <div className="questionnaire-logo-title">
              <VocreaLogo />
              <h1 className="questionnaire-app-title">Vocrea AI</h1>
            </div>
          </div>

          {/* Main Card */}
          <div className="questionnaire-card-modern">
            {/* Progress Bar */}
            <div className="questionnaire-progress-section">
              <div className="questionnaire-progress-header">
                <button className="questionnaire-back-button" onClick={handleBack}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18L9 12L15 6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Back</span>
                </button>
                <span className="questionnaire-step-indicator">
                  Step {currentStep} of {totalSteps}
                </span>
              </div>
              <div className="questionnaire-progress-bar-container">
                <div className="questionnaire-progress-bar" style={{ width: `${progressPercentage}%` }} />
              </div>
            </div>

            {/* Section Title */}
            <h2 className="questionnaire-section-title">Cultural & Personal Background</h2>
            <p className="section-description">
              These questions are optional and will help us match you with
              clients who may benefit from shared cultural understanding or
              religious compatibility.
            </p>

            {/* Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (isBackgroundValid) {
                  handleNext();
                }
              }}
              className="questionnaire-form-modern"
            >

              <div className="specialization-group">
                <h3 className="section-subtitle">
                  11. Religious/Spiritual Background
                </h3>
                <p className="section-description">Optional</p>
                <CheckboxGroup
                  items={RELIGIOUS_BACKGROUNDS}
                  selectedItems={
                    formData.personalBackground.religiousBackground || []
                  }
                  allowOther
                  onSelectionChange={handleReligiousBackgroundChange}
                />
              </div>

              <div className="specialization-group">
                <h3 className="section-subtitle">
                  12. Cultural/Ethnic Background
                </h3>
                <p className="section-description">Optional</p>
                <CheckboxGroup
                  items={CULTURAL_BACKGROUNDS}
                  selectedItems={
                    formData.personalBackground.culturalBackground || []
                  }
                  allowOther
                  onSelectionChange={handleCulturalBackgroundChange}
                />
              </div>

              <div className="specialization-group">
                <h3 className="section-subtitle">13. Languages Spoken</h3>
                <p className="section-description">Select all that apply</p>
                <CheckboxGroup
                  items={LANGUAGES}
                  selectedItems={formData.personalBackground.languages}
                  allowOther
                  onSelectionChange={handleLanguagesChange}
                />
              </div>

              <div className="specialization-group">
                <h3 className="section-subtitle">14. Sexual Orientation</h3>
                <p className="section-description">Optional</p>
                <CheckboxGroup
                  items={SEXUAL_ORIENTATIONS}
                  selectedItems={
                    formData.personalBackground.sexualOrientation || []
                  }
                  allowOther
                  onSelectionChange={handleSexualOrientationChange}
                />
              </div>

              {!isBackgroundValid && (
                <div className="questionnaire-error-message">{getBackgroundError()}</div>
              )}

              <button
                type="submit"
                className="questionnaire-button-primary"
                disabled={!isBackgroundValid}
              >
                Next
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (currentSection === "approach") {
    return (
      <div className="questionnaire-container-modern">
        <div className="questionnaire-wrapper-modern">
          {/* Header with Logo */}
          <div className="questionnaire-header-modern">
            <div className="questionnaire-logo-title">
              <VocreaLogo />
              <h1 className="questionnaire-app-title">Vocrea AI</h1>
            </div>
          </div>

          {/* Main Card */}
          <div className="questionnaire-card-modern">
            {/* Progress Bar */}
            <div className="questionnaire-progress-section">
              <div className="questionnaire-progress-header">
                <button className="questionnaire-back-button" onClick={handleBack}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18L9 12L15 6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Back</span>
                </button>
                <span className="questionnaire-step-indicator">
                  Step {currentStep} of {totalSteps}
                </span>
              </div>
              <div className="questionnaire-progress-bar-container">
                <div className="questionnaire-progress-bar" style={{ width: `${progressPercentage}%` }} />
              </div>
            </div>

            {/* Section Title */}
            <h2 className="questionnaire-section-title">Practice Approach & Philosophy</h2>

            {/* Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (isApproachValid) {
                  handleNext();
                }
              }}
              className="questionnaire-form-modern"
            >

              <div className="specialization-group">
                <h3 className="section-subtitle">
                  15. Therapeutic Approach Style
                </h3>
                <p className="section-description">
                  Select one that best describes your style
                </p>
                <CheckboxGroup
                  items={THERAPEUTIC_STYLES}
                  selectedItems={formData.practiceApproach.therapeuticStyle}
                  maxSelections={1}
                  minSelections={1}
                  onSelectionChange={handleTherapeuticStyleChange}
                />
              </div>

              <div className="specialization-group">
                <h3 className="section-subtitle">
                  16. Integration of Spirituality/Religion in Therapy
                </h3>
                <p className="section-description">
                  Select one that best describes your approach
                </p>
                <CheckboxGroup
                  items={SPIRITUALITY_APPROACHES}
                  selectedItems={formData.practiceApproach.spiritualityApproach}
                  maxSelections={1}
                  minSelections={1}
                  onSelectionChange={handleSpiritualityApproachChange}
                />
              </div>

              <div className="specialization-group">
                <h3 className="section-subtitle">
                  17. Issues You Do NOT Treat
                </h3>
                <p className="section-description">Select all that apply</p>
                <CheckboxGroup
                  items={NON_TREATMENT_ISSUES}
                  selectedItems={formData.practiceApproach.nonTreatmentIssues}
                  allowOther
                  onSelectionChange={handleNonTreatmentIssuesChange}
                />
              </div>

              {!isApproachValid && (
                <div className="questionnaire-error-message">{getApproachError()}</div>
              )}

              <button
                type="submit"
                className="questionnaire-button-primary"
                disabled={!isApproachValid}
              >
                Next
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (currentSection === "additional") {
    return (
      <div className="questionnaire-container-modern">
        <div className="questionnaire-wrapper-modern">
          {/* Header with Logo */}
          <div className="questionnaire-header-modern">
            <div className="questionnaire-logo-title">
              <VocreaLogo />
              <h1 className="questionnaire-app-title">Vocrea AI</h1>
            </div>
          </div>

          {/* Main Card */}
          <div className="questionnaire-card-modern">
            {/* Progress Bar */}
            <div className="questionnaire-progress-section">
              <div className="questionnaire-progress-header">
                <button className="questionnaire-back-button" onClick={handleBack}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18L9 12L15 6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Back</span>
                </button>
                <span className="questionnaire-step-indicator">
                  Step {currentStep} of {totalSteps}
                </span>
              </div>
              <div className="questionnaire-progress-bar-container">
                <div className="questionnaire-progress-bar" style={{ width: `${progressPercentage}%` }} />
              </div>
            </div>

            {/* Section Title */}
            <h2 className="questionnaire-section-title">Additional Information</h2>

            {/* Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleNext();
              }}
              className="questionnaire-form-modern"
            >
              {error && (
                <div className="questionnaire-error-message" role="alert">
                  {error}
                </div>
              )}

              <div className="specialization-group">
                <h3 className="section-subtitle">
                  18. Special Certifications or Training
                </h3>
                <p className="section-description">
                  List any additional certifications, specialized training, or
                  areas of expertise
                </p>
                <textarea className="questionnaire-textarea" 
                  value={formData.additionalInformation.specialCertifications}
                  onChange={handleSpecialCertificationsChange}
                  rows={4}
                  placeholder="Enter your special certifications or training..."
                />
              </div>

              <div className="specialization-group">
                <h3 className="section-subtitle">
                  19. Professional Memberships
                </h3>
                <p className="section-description">Select all that apply</p>
                <CheckboxGroup
                  items={PROFESSIONAL_MEMBERSHIPS}
                  selectedItems={
                    formData.additionalInformation.professionalMemberships
                  }
                  allowOther
                  onSelectionChange={handleProfessionalMembershipsChange}
                />
              </div>

              <div className="specialization-group">
                <h3 className="section-subtitle">
                  20. Anything else you'd like to say to your clients
                </h3>
                <p className="section-description">
                  Would you like to add a self-intro that could help us make
                  better matches?
                </p>
                <textarea className="questionnaire-textarea" 
                  value={formData.additionalInformation.additionalNotes}
                  onChange={handleAdditionalNotesChange}
                  rows={4}
                  placeholder="Enter any additional notes..."
                />
              </div>

              <button
                type="submit"
                className="questionnaire-button-primary"
                disabled={isSubmitting}
              >
                Submit
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="questionnaire-container">
      <div className="questionnaire-header">
        <button className="back-button" onClick={handleBack}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 className="questionnaire-title">Professional Qualification</h1>
      </div>

      <div className="questionnaire-content">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (isSpecializationsValid) {
              handleNext();
            }
          }}
          className="questionnaire-form-modern"
        >
          <div className="form-section">
            <h2 className="section-title">Therapeutic Specializations</h2>

            <h3 className="section-subtitle">
              5. Primary Therapeutic Modalities
            </h3>
            <p className="section-description">Select 1-3 primary modalities</p>
            <CheckboxGroup
              items={THERAPY_MODALITIES}
              selectedItems={formData.therapeuticSpecializations.topModalities}
              onSelectionChange={handleTopModalitiesChange}
              maxSelections={3}
              minSelections={1}
              showYearsInput
              allowOther
            />

            <h3 className="section-subtitle">All Trained Therapy Modalities</h3>
            <p className="section-description">Select all that apply</p>
            <CheckboxGroup
              items={THERAPY_MODALITIES}
              selectedItems={
                formData.therapeuticSpecializations.allTrainedModalities
              }
              onSelectionChange={handleAllTrainedModalitiesChange}
              showYearsInput
              allowOther
              lockedItems={formData.therapeuticSpecializations.topModalities.map(
                (item) => item.name
              )}
            />

            <h3 className="section-subtitle">
              Primary Clinical Specializations
            </h3>
            <p className="section-description">
              Select 1-3 primary specializations
            </p>
            <CheckboxGroup
              items={CLINICAL_SPECIALIZATIONS}
              selectedItems={
                formData.therapeuticSpecializations.topSpecializations
              }
              onSelectionChange={handleTopSpecializationsChange}
              maxSelections={3}
              minSelections={1}
              showYearsInput
              allowOther
            />

            <h3 className="section-subtitle">All Clinical Areas</h3>
            <p className="section-description">Select all that apply</p>
            <CheckboxGroup
              items={CLINICAL_SPECIALIZATIONS}
              selectedItems={
                formData.therapeuticSpecializations.allClinicalAreas
              }
              onSelectionChange={handleAllClinicalAreasChange}
              showYearsInput
              allowOther
              lockedItems={formData.therapeuticSpecializations.topSpecializations.map(
                (item) => item.name
              )}
            />
          </div>

          {!isSpecializationsValid && (
            <div className="error-message">{getSpecializationsError()}</div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={handleBack}
            >
              Previous
            </button>
            <button
              type="submit"
              className={`primary-button ${
                !isSpecializationsValid ? "disabled" : ""
              }`}
              disabled={!isSpecializationsValid}
            >
              Next
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuestionnairePage;
